import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Visibility,
  Download,
  Receipt,
  QrCode,
  Search,
  FilterList,
  CalendarToday,
  Group,
  Euro,
  MoreVert,
  Payment,
  Cancel,
  CheckCircle
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { API_BASE_URL } from '../../config/api.config';

interface Booking {
  id: number;
  bookingNumber: string;
  scheduleDate: string;
  startTime: string;
  variant: 'Standard' | 'Premium';
  tourTypeCode?: string; // Code of the tour type (e.g., "KAFFEE-KUCHEN-FLOSSFAHRT")
  tourTypeName?: string; // Display name of the tour type (e.g., "Premium Floßfahrt Montag-Donnerstag")
  participantCount: number;
  totalAmount: number;
  voucherDiscount?: number; // Amount redeemed from voucher
  finalAmount?: number; // Final amount after voucher discount
  status: 'Pending' | 'Confirmed' | 'Paid' | 'Cancelled' | 'Completed';
  paymentStatus: 'Pending' | 'PartiallyPaid' | 'Paid' | 'Refunded';
  createdAt: string;
  createdAtTime?: string; // Time when booking was created (HH:mm:ss)
  hasTicket: boolean;
  hasInvoice: boolean;
}

type Order = 'asc' | 'desc';
type OrderBy = 'bookingNumber' | 'scheduleDate' | 'tourTypeCode' | 'participantCount' | 'finalAmount' | 'status' | 'paymentStatus' | 'createdAt';

const BookingHistory: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalBookings, setTotalBookings] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('createdAt');

  useEffect(() => {
    loadBookings();
  }, [page, rowsPerPage, statusFilter, order, orderBy]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken'); // Changed from 'accessToken' to 'authToken'

      // Skip loading if no token (user not authenticated)
      if (!token) {
        console.log('No auth token, skipping booking load');
        setBookings([]);
        setTotalBookings(0);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        pageSize: rowsPerPage.toString(),
        sortBy: orderBy,
        sortOrder: order,
        ...(statusFilter !== 'all' && { status: statusFilter })
      });

      const response = await fetch(`/api/user/bookings?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Buchungen');
      }

      const data = await response.json();
      setBookings(data.items || []);
      setTotalBookings(data.totalCount || 0);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError('Buchungen konnten nicht geladen werden');
      
      // Mock data for development
      setBookings([
        {
          id: 1,
          bookingNumber: 'BK-2025-0001',
          scheduleDate: '2025-10-15',
          startTime: '14:00',
          variant: 'Premium',
          tourTypeCode: 'KAFFEE-KUCHEN-FLOSSFAHRT',
          tourTypeName: 'Premium Floßfahrt Montag-Donnerstag',
          participantCount: 45,
          totalAmount: 2925.00,
          voucherDiscount: 100.00,
          finalAmount: 2825.00,
          status: 'Confirmed',
          paymentStatus: 'PartiallyPaid',
          createdAt: '2025-09-01',
          createdAtTime: '10:30:45',
          hasTicket: false,
          hasInvoice: true
        },
        {
          id: 2,
          bookingNumber: 'BK-2025-0002',
          scheduleDate: '2025-08-20',
          startTime: '11:00',
          variant: 'Standard',
          tourTypeCode: 'STANDARD-FLOSSFAHRT',
          tourTypeName: 'Standard Floßfahrt',
          participantCount: 30,
          totalAmount: 1500.00,
          voucherDiscount: 0,
          finalAmount: 1500.00,
          status: 'Completed',
          paymentStatus: 'Paid',
          createdAt: '2025-07-15',
          createdAtTime: '14:30:22',
          hasTicket: true,
          hasInvoice: true
        }
      ]);
      setTotalBookings(2);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed': return 'success';
      case 'Pending': return 'warning';
      case 'Paid': return 'success';
      case 'Cancelled': return 'error';
      case 'Completed': return 'info';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'PartiallyPaid': return 'warning';
      case 'Pending': return 'error';
      case 'Refunded': return 'default';
      default: return 'default';
    }
  };

  // German status labels
  const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'Pending': 'Ausstehend',
      'Draft': 'Entwurf',
      'Option': 'Option',
      'OfferSent': 'Angebot gesendet',
      'OfferAccepted': 'Angebot angenommen',
      'OfferDeclined': 'Angebot abgelehnt',
      'OfferExpired': 'Angebot abgelaufen',
      'Confirmed': 'Bestätigt',
      'Paid': 'Bezahlt',
      'Completed': 'Abgeschlossen',
      'Cancelled': 'Storniert',
      'NoShow': 'Nicht erschienen',
      'Refunded': 'Erstattet'
    };
    return statusLabels[status] || status;
  };

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  // Determine available actions based on booking status
  const getAvailableActions = (booking: Booking) => {
    const actions = {
      canView: true,
      canPay: ['Confirmed', 'OfferAccepted'].includes(booking.status) && booking.paymentStatus !== 'Paid',
      canDownloadInvoice: booking.hasInvoice,
      canDownloadTicket: booking.hasTicket && booking.paymentStatus === 'Paid',
      canCancel: ['Pending', 'Confirmed', 'Option', 'OfferAccepted'].includes(booking.status)
    };
    return actions;
  };

  const handleViewDetails = (bookingId: number) => {
    // Navigate to booking confirmation/details page
    window.location.href = `/booking-confirmation/${bookingId}`;
  };

  const handleDownloadInvoice = async (bookingId: number) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/user/bookings/${bookingId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const handleDownloadTicket = async (bookingId: number) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/user/bookings/${bookingId}/ticket`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ticket-${bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading ticket:', error);
    }
  };

  // Sorting handler - resets page to 0 for server-side sorting across all pages
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting changes
  };

  // Client-side filtering only (sorting is done server-side)
  const filteredBookings = bookings
    .filter(booking =>
      booking.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.scheduleDate.includes(searchTerm)
    );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Meine Buchungen
      </Typography>
      
      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Suche nach Buchungsnummer oder Datum..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="Confirmed">Bestätigt</MenuItem>
            <MenuItem value="Pending">Ausstehend</MenuItem>
            <MenuItem value="Completed">Abgeschlossen</MenuItem>
            <MenuItem value="Cancelled">Storniert</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredBookings.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Keine Buchungen gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sie haben noch keine Floßfahrten gebucht.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} href="/booking">
            Jetzt buchen
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'bookingNumber'}
                      direction={orderBy === 'bookingNumber' ? order : 'asc'}
                      onClick={() => handleRequestSort('bookingNumber')}
                    >
                      Buchungsnr.
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'scheduleDate'}
                      direction={orderBy === 'scheduleDate' ? order : 'asc'}
                      onClick={() => handleRequestSort('scheduleDate')}
                    >
                      Datum & Zeit
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'tourTypeCode'}
                      direction={orderBy === 'tourTypeCode' ? order : 'asc'}
                      onClick={() => handleRequestSort('tourTypeCode')}
                    >
                      Variante
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'participantCount'}
                      direction={orderBy === 'participantCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('participantCount')}
                    >
                      Personen
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'finalAmount'}
                      direction={orderBy === 'finalAmount' ? order : 'asc'}
                      onClick={() => handleRequestSort('finalAmount')}
                    >
                      Betrag
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'paymentStatus'}
                      direction={orderBy === 'paymentStatus' ? order : 'asc'}
                      onClick={() => handleRequestSort('paymentStatus')}
                    >
                      Zahlung
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBookings.map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {booking.bookingNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(booking.createdAt), 'dd.MM.yyyy', { locale: de })} {booking.createdAtTime || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2">
                            {format(new Date(booking.scheduleDate), 'dd. MMMM yyyy', { locale: de })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.startTime} Uhr
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                          {booking.tourTypeCode || booking.variant}
                        </Typography>
                        {booking.tourTypeName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {booking.tourTypeName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <Group fontSize="small" color="action" />
                        <Typography variant="body2">{booking.participantCount}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(booking.finalAmount ?? booking.totalAmount)}
                        </Typography>
                        {(booking.voucherDiscount ?? 0) > 0 && (
                          <Typography variant="caption" color="success.main" display="block">
                            (-{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(booking.voucherDiscount!)} Gutschein)
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getStatusLabel(booking.status)}
                        size="small"
                        color={getStatusColor(booking.status) as any}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          booking.paymentStatus === 'Paid' ? 'Bezahlt' :
                          booking.paymentStatus === 'PartiallyPaid' ? 'Teilzahlung' :
                          booking.paymentStatus === 'Pending' ? 'Ausstehend' :
                          'Erstattet'
                        }
                        size="small"
                        color={getPaymentStatusColor(booking.paymentStatus) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking)}
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: '8px'
              }
            }}
          >
            {selectedBooking && (() => {
              const actions = getAvailableActions(selectedBooking);
              return (
                <>
                  <MenuItem onClick={() => { handleViewDetails(selectedBooking.id); handleMenuClose(); }}>
                    <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                    <ListItemText>Details anzeigen</ListItemText>
                  </MenuItem>

                  {actions.canPay && (
                    <MenuItem onClick={() => { window.location.href = `/checkout/${selectedBooking.id}`; handleMenuClose(); }}>
                      <ListItemIcon><Payment fontSize="small" sx={{ color: '#7B3F00' }} /></ListItemIcon>
                      <ListItemText>Jetzt bezahlen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canDownloadInvoice && (
                    <MenuItem onClick={() => { handleDownloadInvoice(selectedBooking.id); handleMenuClose(); }}>
                      <ListItemIcon><Receipt fontSize="small" /></ListItemIcon>
                      <ListItemText>Rechnung herunterladen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canDownloadTicket && (
                    <MenuItem onClick={() => { handleDownloadTicket(selectedBooking.id); handleMenuClose(); }}>
                      <ListItemIcon><QrCode fontSize="small" /></ListItemIcon>
                      <ListItemText>Ticket herunterladen</ListItemText>
                    </MenuItem>
                  )}

                  {actions.canCancel && (
                    <>
                      <Divider />
                      <MenuItem
                        onClick={() => { alert('Stornierung: Bitte kontaktieren Sie uns unter +49 170 7744661'); handleMenuClose(); }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon><Cancel fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                        <ListItemText>Stornieren</ListItemText>
                      </MenuItem>
                    </>
                  )}
                </>
              );
            })()}
          </Menu>

          <TablePagination
            component="div"
            count={totalBookings}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Einträge pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default BookingHistory;
